import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-h6 q-px-md q-py-md"
}

import { PropType, watch, ref, onMounted, computed } from "vue";
  import { formatTimeAgo, useTimeAgo } from "@vueuse/core";
  import { QInput, QSelect } from "quasar";

  import { useAuthStore } from "src/stores/auth-store";

  import { useCapitalise, useDifferenceInDays } from "src/functions";

  import { LoanApplication } from "src/types/loan-application/index";
  import { LoanStatusType_Enum, LoanDecisionType_Enum } from "src/types/enums/index";
  import { DrivingLicenceOptions, MaritalStatusOptions, TitleOptions } from "src/const/select-options"
  import DataTable from "./DataTable.vue";
  import { useCurrencyFormatter, useDateString, useDifferenceInMinutes } from "src/functions";
  //@ts-ignore
  import { calculateMonthlyPaymentForVehicle } from "@cf247/pricing/src/monthly-payment-calculator"
  import { useProposalStore } from "src/stores/proposal-store";
import { useSettingsStore } from "src/stores/settings-store";
  
  
export default /*@__PURE__*/_defineComponent({
  __name: 'SummaryDetailsForm',
  props: {
    proposal: {
      type: Object as PropType<LoanApplication>,
      default: {} as LoanApplication
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    basic: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  setup(__props) {

  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();

  const props = __props

  const dataTableData = computed(() => {
    let d = [
      { label: "Dealer Agent", value: sanitiseName(props.proposal.createdBy), html: false },
      { label: "Decision", value: decisionStatus.value!.label, badge: true, badgeClass: decisionStatus.value!.colour },
      { label: "Status", value: LoanStatusType_Enum[props.proposal.state], badge: true, badgeColour: 'red' },
      { label: "Submitted", value: useCapitalise(formatTimeAgo(new Date(props.proposal.created))) },
      { label: "Last Updated", value: useCapitalise(formatTimeAgo(new Date(props.proposal.lastUpdated))) },
      {
        label: "Broker Reference",
        value: props.proposal.brokerReference && props.proposal.brokerReference > 0
          ? props.proposal.brokerReference.toString()
          : props.proposal.brokerReference === 0 && isOldApp ?
            `<span>Unable to retrieve Broker Reference</span>` :
            `<svg class="q-spinner text-primary q-spinner-mat" width="1em" height="1em" viewBox="25 25 50 50"><circle class="path" cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5" stroke-miterlimit="10"></circle></svg>`, html: true
      }
    ]
    if (authStore.selectedDealers && authStore.selectedDealers.length > 1) {
      d.push({ label: "Dealer Branch", value: authStore.selectedDealers.find(d => d.affiliateUserId === Number(props.proposal.affiliateUserId))!.name })
    }
    if (props.proposal!.introducerReference) {
      d.push({ label: "Introducer", value: props.proposal.introducerName! })
      d.push({ label: "Introducer Reference", value: props.proposal.introducerReference })
    }
    return d;
  })

  function sanitiseName(name: string) {
    if (name === "Carfinance.Application.Form.Prototype") {
      return "Customer application";
    }
    return name;
  }
  const isOldApp = computed(() => {
    return (Math.abs(new Date(props.proposal.created).getTime() - new Date().getTime()) / 36e5) > 6;
  })
  const status = computed(() => {
    let so = {
      icon: "",
      iconColour: "text-dark",
      label: "",
      badgeColour: "primary"
    }

    if (!props.proposal) {
      return so;
    }

    if (props.proposal.vehicle && props.proposal.vehicle.pcpMatrix && props.proposal.vehicle.pcpMatrix.length > 0) {
      const filteredSortedPcp = props.proposal.vehicle.pcpMatrix.filter(p => availableTerms.value.includes(p.term)).sort((a, b) => a.term < b.term ? -1 : 1);
      so.icon = "mdi mdi-check-circle";
      so.iconColour = "text-positive";
      so.badgeColour = "positive";
      so.label = `Approved (${filteredSortedPcp[filteredSortedPcp.length - 1].financeCompany})`
      return so;
    }

    if (props.proposal.vehicle && props.proposal.vehicle.priceMatrix && props.proposal.vehicle.priceMatrix.length > 0) {
      const filteredSortedHp = props.proposal.vehicle.priceMatrix.filter(p => availableTerms.value.includes(p.term)).sort((a, b) => a.term < b.term ? -1 : 1);
      so.icon = "mdi mdi-checkbox-marked";
      so.iconColour = "text-positive";
      so.badgeColour = "positive";
      so.label = `Approved (${filteredSortedHp[filteredSortedHp.length - 1].financeCompany})`
      return so;
    }

    return so;
  })

  const availableTerms = ref([12, 24, 36, 48, 60])

  const decisionStatus = computed(() => {
    if (props.proposal.proposals && props.proposal.proposals.length > 0) {
      // Approved / Vehicle Failed
      if (props.proposal.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'Accepted')) {
        return {
          label: [3, 0].includes(vehicleValid.value) ? "Approved" : "Failed Vehicle Rule",
          colour: [3, 0].includes(vehicleValid.value) ? "bg-positive text-white" : "bg-warning",
          badgeColour: [3, 0].includes(vehicleValid.value) ? 'positive' : 'warning'
        }
      }

      // Provisional Approval / Vehicle Failed
      if (props.proposal.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'ProvisionalAcceptance')) {
        return {
          label: [3, 0].includes(vehicleValid.value) ? "Approved (Predicted)" : "Failed Vehicle Rule",
          colour: [3, 0].includes(vehicleValid.value) ? "bg-positive  text-white" : "bg-warning",
          badgeColour: [3, 0].includes(vehicleValid.value) ? 'positive' : 'warning'
        }
      }

      // Processing
      if (props.proposal.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'Processing')) {
        return {
          label: "Awaiting Decision",
          colour: "bg-warning text-dark",
          badgeColour: 'warning'
        }
      }

      // Failes Customer Rules
      if (props.proposal.proposals.find(p => (p.customerRuleResults && p.customerRuleResults.find(r => r.resultType === 'FixedDecline')) || (p.loanProposalStatus && p.loanProposalStatus.toString() === 'FailedSendRules'))) {
        return {
          label: "Failed Customer Rules",
          colour: "bg-warning text-dark",
          badgeColour: 'warning'
        }
      }

      // Declined
      if (props.proposal.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'Declined')) {
        return {
          label: "Declined",
          colour: "bg-negative text-white",
          badgeColour: 'negative'
        }
      }
    }
    else {
      // App just created
      if (useDifferenceInMinutes(new Date(props.proposal.created), new Date()) < 15) {
        return {
          label: "Awaiting Decision",
          colour: "bg-warning text-dark",
          badgeColour: 'warning'
        };
      }


      if (!props.proposal.proposals || !props.proposal.proposals.length) {
        return {
          label: "Not approved",
          colour: "bg-negative text-white",
          badgeColour: 'negative'
        };
      }
    }
  })

  const vehicleValid = ref(0);
  function vehiclePasses() {

    if ([
      LoanStatusType_Enum["NTU Rate Too High"],
      LoanStatusType_Enum["NTU Lost To Competitor"],
      LoanStatusType_Enum["NTU Outside Criteria"],
      LoanStatusType_Enum["NTU Other"],
    ].includes(props.proposal.state) || (decisionStatus.value && decisionStatus.value.label === 'Declined')) {
      vehicleValid.value = 0;
      return;
    }

    if (!props.proposal || !props.proposal.vehicle || props.proposal.isQuickQuote) {
      vehicleValid.value = 0;
      return;
    }

    if (!props.proposal || !props.proposal.proposals || props.proposal.proposals.length === 0) {
      vehicleValid.value = 0;
      return;
    }

    const v = calculateMonthlyPaymentForVehicle(
      props.proposal.vehicle,
      props.proposal.proposals,
      props.proposal.deposit,
      props.proposal.pxValue ?? 0,
      props.proposal.customer,
      {
        pxSettlement: props.proposal.settlement ?? 0,
        bespokeRates: useProposalStore().bespokeRates,
        annualMileage: props.proposal.vehicle.annualMileage,
        terms: [24, 36, 48, 60].concat(settingsStore.termsToShow.filter(t => ![24, 36, 48, 60].includes(t))),
        ignorePcpLender:[49],
        ignoreLTV: true,
      }
    )
    if (v.cannotPrice) {
      vehicleValid.value = 2;
      return;
    }

    vehicleValid.value = 3;
  }

return (_ctx: any,_cache: any) => {
  const _component_q_separator = _resolveComponent("q-separator")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (__props.showTitle)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, "Application Details"))
      : _createCommentVNode("", true),
    (__props.showTitle)
      ? (_openBlock(), _createBlock(_component_q_separator, {
          key: 1,
          style: {"border-width":"3px !important"}
        }))
      : _createCommentVNode("", true),
    _createVNode(DataTable, { data: dataTableData.value }, null, 8, ["data"])
  ], 64))
}
}

})